import L from 'leaflet';
import { activeMapConfig } from './map';

/**
 * Gemeinsame Leaflet-Bausteine. Wir nutzen ausschließlich `divIcon`, damit
 * Leaflet keine Marker-Grafiken nachladen muss (die Standardpfade laufen im
 * Bundle sonst ins Leere).
 */

export type MarkerKind = 'driver' | 'pickup' | 'origin';

const GLYPHS: Record<MarkerKind, string> = {
  driver: '&#128663;',  // Auto
  pickup: '&#128100;',  // Person
  origin: '&#127968;',  // Haus
};

export function markerIcon(kind: MarkerKind): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div class="r2f-marker r2f-marker--${kind}">${GLYPHS[kind]}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

/**
 * Die Karte, die der Server vorgibt.
 *
 * Ohne Konfiguration ist das CARTO "Positron": bewusst zurückhaltend - helle
 * Flächen, dünne Straßen, sparsame Beschriftung, keine
 * Sehenswürdigkeiten-Symbole. Damit tritt die Karte hinter das zurück, worum
 * es geht: Marker, Route, Fahrtverlauf. Und sie braucht wie die OSM-Kacheln
 * keinen API-Schlüssel.
 *
 * Wer einen bezahlten Kartendienst nutzt, hinterlegt den Schlüssel im Docker
 * (`MAP_API_KEY`) oder - in der Android-App - in den Einstellungen. Was dann
 * geladen wird, entscheidet lib/map.ts.
 */
export function baseTileLayer(): L.TileLayer {
  const config = activeMapConfig();
  return L.tileLayer(config.tileUrl, {
    ...(config.subdomains ? { subdomains: config.subdomains } : {}),
    maxZoom: config.maxZoom,
    attribution: config.attribution,
  });
}

/**
 * Die Verbindung zwischen zwei Punkten.
 *
 * Bewusst gestrichelt: das ist die **Luftlinie**, keine gefahrene Route. Ein
 * echtes Routing bräuchte einen Routing-Dienst, den dieses Projekt bewusst
 * nicht hat (siehe docs/architecture.md). Die echte Route liefert ein Tippen
 * auf "Route öffnen" in Google Maps.
 */
export function directLine(from: L.LatLngExpression, to: L.LatLngExpression): L.Polyline {
  return L.polyline([from, to], {
    // Tinte statt Farbe: auf hellen Kacheln liest sich die dunkle Linie
    // deutlich besser als ein Pastellton.
    color: '#14161c',
    weight: 3,
    opacity: 0.7,
    dashArray: '2 9',
    lineCap: 'round',
  });
}

/** Navigation im jeweiligen Kartendienst - öffnet ausserhalb der App. */
export function navigationUrl(
  from: { lat: number; lng: number } | null,
  to: { lat: number; lng: number },
): string {
  const destination = `${to.lat},${to.lng}`;
  const origin = from ? `&origin=${from.lat},${from.lng}` : '';
  return `https://www.google.com/maps/dir/?api=1${origin}&destination=${destination}&travelmode=driving`;
}

/** Karte so setzen, dass alle übergebenen Punkte sichtbar sind. */
export function fitToPoints(map: L.Map, points: L.LatLngExpression[], fallbackZoom = 15): void {
  if (points.length === 0) return;
  if (points.length === 1) {
    map.setView(points[0], fallbackZoom);
    return;
  }
  map.fitBounds(L.latLngBounds(points), { padding: [36, 36], maxZoom: 16 });
}
